// Formulier om een activiteit toe te voegen of te bewerken (Modal).
import { lazy, Suspense, useId, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { LocateFixed, MapPin, Medal, Trash2, X } from 'lucide-react';
import { Modal } from '../../components/Modal';
import { ToggleChip } from '../../components/ui/ToggleChip';
import { ACTIVITY_DUUR_MAX, ACTIVITY_DUUR_MIN, ACTIVITY_FIELD_LIMITS, getMainCategory } from '../../domain/activities';
import {
  activityToInput,
  EMPTY_ACTIVITY_INPUT,
  PERSONS_OPTIONS,
  validateActivityInput,
  type ActivityInput,
  type ActivityInputField,
  type PersonsOption,
} from '../../domain/activityForm';
import type { Activity } from '../../domain/types';
import { useActivities, useToast } from '../../state/AppProviders';
import { MAIN_CATEGORY_THEMES } from '../../utils/categoryThemes';
import { ConfirmDialog, type ConfirmRequest } from '../gameover/ConfirmDialog';
import { BTN_DANGER, BTN_PRIMARY, BTN_TONAL, chipClass, INPUT } from '../gameover/ui';

const LocationPicker = lazy(() => import('./LocationPicker'));

const QUICK_DURATIONS = [15, 30, 45, 60, 90] as const;
const labelClass = 'mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-300';
const errorClass = 'mt-1.5 text-sm font-medium text-rose-700 dark:text-rose-300';
const hintClass = 'mt-1.5 text-sm text-slate-600 dark:text-slate-400';
const invalidRing = 'ring-2 ring-rose-600 dark:ring-rose-400';

export interface ActivityEditorProps {
  /** Aanwezig = bewerken, anders nieuw. */
  activity?: Activity;
  onClose: () => void;
  onSaved?: (a: Activity) => void;
}

interface SegmentedProps<T extends string> {
  label: string;
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (v: T) => void;
}

function Segmented<T extends string>({ label, value, options, onChange }: SegmentedProps<T>) {
  return (
    <div role="radiogroup" aria-label={label} className="grid auto-cols-fr grid-flow-col gap-2">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={`${chipClass(value === o.value)} justify-center`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Field({ label, htmlFor, children }: { label: string; htmlFor?: string; children: ReactNode }) {
  return (
    <div>
      {htmlFor ? (
        <label htmlFor={htmlFor} className={labelClass}>
          {label}
        </label>
      ) : (
        <p className={labelClass}>{label}</p>
      )}
      {children}
    </div>
  );
}

export function ActivityEditor({ activity, onClose, onSaved }: ActivityEditorProps) {
  const { activities, addActivity, updateActivity, deleteActivity } = useActivities();
  const { showToast } = useToast();
  const uid = useId();
  const editing = !!activity;

  const initial = useMemo<ActivityInput>(
    () => (activity ? activityToInput(activity) : EMPTY_ACTIVITY_INPUT),
    [activity],
  );
  const [input, setInput] = useState<ActivityInput>(initial);
  const [duurText, setDuurText] = useState(String(initial.duur));
  const [touched, setTouched] = useState<Partial<Record<ActivityInputField, boolean>>>({});
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [geoMessage, setGeoMessage] = useState<string | null>(null);
  const [geoBusy, setGeoBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmRequest | null>(null);

  const dirty = useMemo(
    () =>
      JSON.stringify({ ...input, duur: duurText.trim() }) !==
      JSON.stringify({ ...initial, duur: String(initial.duur) }),
    [input, duurText, initial],
  );

  const result = useMemo(
    () =>
      validateActivityInput(input, {
        existing: activities,
        editingId: activity?.id,
      }),
    [input, activities, activity?.id],
  );
  const show = (f: ActivityInputField) => (submitted || touched[f] ? result.errors[f] : undefined);
  const patch = (p: Partial<ActivityInput>) => setInput((i) => ({ ...i, ...p }));
  const touch = (f: ActivityInputField) => setTouched((t) => ({ ...t, [f]: true }));

  const requestClose = () => {
    if (dirty && !saving) {
      setConfirm({
        title: 'Wijzigingen weggooien?',
        message: 'Je wijzigingen zijn nog niet bewaard.',
        confirmText: 'Weggooien',
        onConfirm: onClose,
      });
    } else onClose();
  };

  const setDuur = (text: string) => {
    setDuurText(text);
    patch({
      duur: text.trim() === '' ? Number.NaN : Number(text.trim().replace(',', '.')),
    });
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSubmitted(true);
    setSaveError(false);
    const order: ActivityInputField[] = ['naam', 'omschrijving', 'duur', 'locatie'];
    const first = order.find((k) => result.errors[k]);
    if (first) {
      document.getElementById(`${uid}-${first}`)?.focus();
      return;
    }
    setSaving(true);
    try {
      const saved = editing ? await updateActivity(activity.id, input) : await addActivity(input);
      showToast(editing ? 'Wijzigingen bewaard' : 'Activiteit toegevoegd');
      onSaved?.(saved);
      onClose();
    } catch {
      setSaving(false);
      setSaveError(true);
    }
  };

  const onDelete = () => {
    if (!activity) return;
    setConfirm({
      title: 'Activiteit verwijderen?',
      message: '{naam} wordt uit je lijst gehaald. Dit kun je niet ongedaan maken.',
      naam: activity.naam,
      confirmText: 'Verwijderen',
      onConfirm: async () => {
        try {
          await deleteActivity(activity.id);
          showToast('Activiteit verwijderd');
          onClose();
        } catch {
          setSaveError(true);
        }
      },
    });
  };

  const useMyLocation = () => {
    setGeoMessage(null);
    if (!('geolocation' in navigator)) {
      setGeoMessage('Je toestel of browser deelt geen locatie. Kies de plek op de kaart of typ de coördinaten.');
      return;
    }
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeoBusy(false);
        patch({
          locatie: `${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)}`,
        });
        touch('locatie');
      },
      (err) => {
        setGeoBusy(false);
        setGeoMessage(
          err.code === 1
            ? 'Toegang tot je locatie is geweigerd. Je kunt dit in je browserinstellingen aanpassen, of kies de plek op de kaart.'
            : 'Je locatie kon niet worden bepaald. Probeer het later opnieuw of kies de plek op de kaart.',
        );
      },
      { timeout: 10000, maximumAge: 60000 },
    );
  };

  const category = getMainCategory(input.type, input.energie, undefined);
  const theme = MAIN_CATEGORY_THEMES[category];
  const CategoryIcon = theme.icon;
  const ids = {
    naam: `${uid}-naam`,
    omschrijving: `${uid}-omschrijving`,
    duur: `${uid}-duur`,
    locatie: `${uid}-locatie`,
  };
  const err = {
    naam: show('naam'),
    omschrijving: show('omschrijving'),
    duur: show('duur'),
    locatie: show('locatie'),
  };
  const aria = (f: ActivityInputField) => ({
    'aria-invalid': err[f] ? (true as const) : undefined,
    'aria-describedby': err[f] ? `${ids[f]}-err` : undefined,
    onBlur: () => touch(f),
  });
  const duurNumber = Number(duurText.trim().replace(',', '.'));

  return (
    <>
      <Modal title={editing ? 'Activiteit bewerken' : 'Nieuwe activiteit'} onClose={requestClose}>
        <form onSubmit={(e) => void submit(e)} noValidate className="space-y-5">
          <div
            className={`flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-semibold ${theme.pillBg} ${theme.accent}`}
          >
            <CategoryIcon size={18} aria-hidden="true" className="shrink-0" />
            <span className="whitespace-nowrap">{theme.label}</span>
            {activity && (
              <span className="ml-auto flex items-center gap-1.5 whitespace-nowrap">
                <Medal size={16} aria-hidden="true" />
                {activity.score} keer gedaan
              </span>
            )}
          </div>

          <Field label="Naam" htmlFor={ids.naam}>
            <input
              id={ids.naam}
              type="text"
              autoFocus
              required
              autoComplete="off"
              maxLength={ACTIVITY_FIELD_LIMITS.naam}
              value={input.naam}
              onChange={(e) => patch({ naam: e.target.value })}
              placeholder="Bijv. Fietstocht langs de IJssel"
              {...aria('naam')}
              className={`${INPUT} ${err.naam ? invalidRing : ''}`}
            />
            {err.naam && (
              <p id={`${ids.naam}-err`} className={errorClass}>
                {err.naam}
              </p>
            )}
            {result.warnings.map((w) => (
              <p key={w} className={hintClass}>
                {w}
              </p>
            ))}
          </Field>

          <Field label="Omschrijving (niet verplicht)" htmlFor={ids.omschrijving}>
            <textarea
              id={ids.omschrijving}
              rows={3}
              maxLength={ACTIVITY_FIELD_LIMITS.omschrijving}
              value={input.omschrijving}
              onChange={(e) => patch({ omschrijving: e.target.value })}
              {...aria('omschrijving')}
              className={`${INPUT} min-h-24 resize-y py-3 ${err.omschrijving ? invalidRing : ''}`}
            />
            <div className="flex items-start justify-between gap-3">
              {err.omschrijving ? (
                <p id={`${ids.omschrijving}-err`} className={errorClass}>
                  {err.omschrijving}
                </p>
              ) : (
                <span />
              )}
              <p className={`${hintClass} shrink-0 whitespace-nowrap`}>
                {input.omschrijving.length} / {ACTIVITY_FIELD_LIMITS.omschrijving}
              </p>
            </div>
          </Field>

          <Field label="Duur (in minuten)" htmlFor={ids.duur}>
            <input
              id={ids.duur}
              type="number"
              inputMode="numeric"
              min={ACTIVITY_DUUR_MIN}
              max={ACTIVITY_DUUR_MAX}
              value={duurText}
              onChange={(e) => setDuur(e.target.value)}
              {...aria('duur')}
              className={`${INPUT} ${err.duur ? invalidRing : ''}`}
            />
            <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label="Snelle duur">
              {QUICK_DURATIONS.map((d) => (
                <button
                  key={d}
                  type="button"
                  aria-pressed={duurNumber === d}
                  aria-label={`${d} minuten`}
                  onClick={() => {
                    setDuur(String(d));
                    touch('duur');
                  }}
                  className={chipClass(duurNumber === d)}
                >
                  {d}m
                </button>
              ))}
            </div>
            {err.duur && (
              <p id={`${ids.duur}-err`} className={errorClass}>
                {err.duur}
              </p>
            )}
          </Field>

          <Field label="Waar">
            <Segmented
              label="Binnen of buiten"
              value={input.type}
              onChange={(type) => patch({ type })}
              options={[
                { value: 'Binnen', label: 'Binnen' },
                { value: 'Buiten', label: 'Buiten' },
              ]}
            />
          </Field>

          <Field label="Energie">
            <Segmented
              label="Energie"
              value={input.energie}
              onChange={(energie) => patch({ energie })}
              options={[
                { value: 'Rustig', label: 'Rustig' },
                { value: 'Veel energie', label: 'Veel energie' },
              ]}
            />
          </Field>

          <Field label="Weer">
            <Segmented
              label="Weer"
              value={input.weer}
              onChange={(weer) => patch({ weer })}
              options={[
                { value: 'Alles', label: 'Elk weer' },
                { value: 'Droog', label: 'Alleen droog' },
              ]}
            />
          </Field>

          <Field label="Aantal spelers">
            <div className="flex flex-wrap gap-2" role="group" aria-label="Aantal spelers">
              {PERSONS_OPTIONS.map((p: PersonsOption) => {
                const on = input.personen.includes(p);
                return (
                  <ToggleChip
                    key={p}
                    pressed={on}
                    ariaLabel={p === '1' ? '1 speler' : p === '2' ? '2 spelers' : '3 of meer spelers'}
                    onToggle={() =>
                      patch({
                        personen: PERSONS_OPTIONS.filter((o) => (o === p ? !on : input.personen.includes(o))),
                      })
                    }
                  >
                    {p}
                  </ToggleChip>
                );
              })}
            </div>
            <p className={hintClass}>
              {input.personen.length === 0
                ? 'Maakt niet uit: past bij elk aantal spelers.'
                : `Past bij ${input.personen.join(', ')} ${input.personen.join('') === '1' ? 'speler' : 'spelers'}.`}
            </p>
          </Field>

          <Field label="Locatie (niet verplicht)" htmlFor={ids.locatie}>
            <input
              id={ids.locatie}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              maxLength={ACTIVITY_FIELD_LIMITS.locatie}
              value={input.locatie}
              onChange={(e) => patch({ locatie: e.target.value })}
              placeholder="52.5168, 6.0924"
              {...aria('locatie')}
              className={`${INPUT} ${err.locatie ? invalidRing : ''}`}
            />
            {err.locatie && (
              <p id={`${ids.locatie}-err`} className={errorClass}>
                {err.locatie}
              </p>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              <button type="button" onClick={() => setPicking(true)} className={BTN_TONAL}>
                <MapPin size={18} aria-hidden="true" /> Kies op kaart
              </button>
              <button type="button" onClick={useMyLocation} disabled={geoBusy} className={BTN_TONAL}>
                <LocateFixed size={18} aria-hidden="true" /> {geoBusy ? 'Zoeken...' : 'Mijn locatie'}
              </button>
              {input.locatie && (
                <button
                  type="button"
                  onClick={() => {
                    patch({ locatie: '' });
                    setGeoMessage(null);
                  }}
                  className={BTN_TONAL}
                >
                  <X size={18} aria-hidden="true" /> Wissen
                </button>
              )}
            </div>
            {geoMessage && (
              <p role="status" className={hintClass}>
                {geoMessage}
              </p>
            )}
          </Field>

          {saveError && (
            <p role="alert" className={errorClass}>
              Dit is niet gelukt. Probeer het opnieuw.
            </p>
          )}

          <div className="flex flex-wrap gap-3 pt-1">
            <button type="submit" disabled={saving} className={`${BTN_PRIMARY} flex-1`}>
              {editing ? 'Bewaren' : 'Toevoegen'}
            </button>
            <button type="button" onClick={requestClose} className={BTN_TONAL}>
              Annuleren
            </button>
          </div>

          {editing && (
            <div className="border-t border-black/5 pt-4 dark:border-white/10">
              <button type="button" onClick={onDelete} className={`${BTN_DANGER} w-full`}>
                <Trash2 size={18} aria-hidden="true" /> Verwijderen
              </button>
            </div>
          )}
        </form>
      </Modal>

      {picking && (
        <Suspense fallback={null}>
          <LocationPicker
            value={input.locatie}
            onClose={() => setPicking(false)}
            onPick={(v) => {
              patch({ locatie: v });
              touch('locatie');
              setPicking(false);
            }}
          />
        </Suspense>
      )}
      {confirm && <ConfirmDialog request={confirm} onClose={() => setConfirm(null)} />}
    </>
  );
}

export default ActivityEditor;
